import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { generateId } from '../lib/utils';
import type { ThemePreference } from '../lib/theme/types';

// Types
export interface HttpRequest {
  id: string;
  name: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
  url: string;
  headers: KeyValue[];
  params: KeyValue[];
  pathParams?: KeyValue[];
  body?: {
    type: 'none' | 'raw' | 'form-data' | 'x-www-form-urlencoded' | 'binary';
    raw?: { content: string; contentType: 'json' | 'xml' | 'text' | 'html' | 'javascript' };
    formData?: KeyValue[];
    urlencoded?: KeyValue[];
  };
  auth?: {
    type: 'none' | 'inherit' | 'bearer' | 'basic' | 'api-key' | 'oauth2' | 'hawk';
    bearer?: { token: string };
    basic?: { username: string; password: string };
    apiKey?: { key: string; value: string; addTo: 'header' | 'query' };
    oauth2?: {
      grantType: 'client_credentials' | 'password' | 'authorization_code';
      accessTokenUrl: string;
      clientId: string;
      clientSecret?: string;
      scope?: string;
      username?: string;
      password?: string;
      accessToken?: string;
      tokenExpiresAt?: number;
    };
    hawk?: {
      authId: string;
      authKey: string;
      algorithm: 'sha256' | 'sha1';
      ext?: string;
      includePayloadHash: boolean;
    };
  };
  scripts?: {
    preRequest?: string;
    postRequest?: string;
  };
}

export interface KeyValue {
  key: string;
  value: string;
  description?: string;
  enabled: boolean;
}

export interface HttpResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  size: number;
  time: number;
}

export interface Tab {
  id: string;
  type: 'request' | 'new';
  title: string;
  request: HttpRequest;
  response?: HttpResponse;
  isDirty: boolean;
  isLoading: boolean;
  collectionId?: string;
  requestId?: string;
}

export interface CollectionAuth {
  type: 'none' | 'bearer' | 'basic' | 'api-key' | 'oauth2' | 'hawk';
  bearer?: { token: string };
  basic?: { username: string; password: string };
  apiKey?: { key: string; value: string; addTo: 'header' | 'query' };
  oauth2?: {
    grantType: 'client_credentials' | 'password' | 'authorization_code';
    accessTokenUrl: string;
    clientId: string;
    clientSecret?: string;
    scope?: string;
    username?: string;
    password?: string;
    accessToken?: string;
    tokenExpiresAt?: number;
  };
  hawk?: {
    authId: string;
    authKey: string;
    algorithm: 'sha256' | 'sha1';
    ext?: string;
    includePayloadHash: boolean;
  };
}

export interface Collection {
  id: string;
  name: string;
  description?: string;
  workspaceId: string;
  folders: Folder[];
  requests: CollectionRequest[];
  variables?: Variable[];
  auth?: CollectionAuth;
}

export interface Folder {
  id: string;
  name: string;
  parentId?: string;
  requests: CollectionRequest[];
}

export interface CollectionRequest {
  id: string;
  name: string;
  method: string;
  folderId?: string;
}

export interface Environment {
  id: string;
  name: string;
  variables: Variable[];
  isGlobal: boolean;
}

export interface Variable {
  key: string;
  value: string;
  isSecret: boolean;
  enabled: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  role: 'owner' | 'admin' | 'member';
  createdAt?: string;
  updatedAt?: string;
}

// Create default request
const createDefaultRequest = (): HttpRequest => ({
  id: generateId(),
  name: 'New Request',
  method: 'GET',
  url: '',
  headers: [],
  params: [],
});

// Create default tab
const createDefaultTab = (): Tab => ({
  id: generateId(),
  type: 'new',
  title: 'New Request',
  request: createDefaultRequest(),
  isDirty: false,
  isLoading: false,
});

// Store types
interface AppState {
  // Workspaces
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  setWorkspaces: (workspaces: Workspace[]) => void;
  setActiveWorkspace: (id: string | null) => void;

  // Collections
  collections: Collection[];
  setCollections: (collections: Collection[]) => void;
  addCollection: (collection: Collection) => void;
  updateCollection: (id: string, updates: Partial<Collection>) => void;
  deleteCollection: (id: string) => void;
  saveRequestToCollection: (collectionId: string, request: HttpRequest, folderId?: string) => string;

  // Folders
  addFolder: (collectionId: string, folder: Folder) => void;
  updateFolder: (collectionId: string, folderId: string, updates: Partial<Folder>) => void;
  deleteFolder: (collectionId: string, folderId: string, moveRequestsToRoot?: boolean) => void;

  // Request Management
  moveRequest: (collectionId: string, requestId: string, targetFolderId: string | null, sourceFolderId: string | null) => void;
  deleteRequest: (collectionId: string, requestId: string, folderId: string | null) => void;
  updateRequestInCollection: (collectionId: string, requestId: string, request: HttpRequest) => void;

  // Tabs
  tabs: Tab[];
  activeTabId: string | null;
  openTab: (tab?: Partial<Tab>) => string;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateTab: (id: string, updates: Partial<Tab>) => void;
  updateTabRequest: (id: string, updates: Partial<HttpRequest>) => void;
  setTabResponse: (id: string, response: HttpResponse) => void;
  setTabLoading: (id: string, isLoading: boolean) => void;

  // Environments
  environments: Environment[];
  activeEnvironmentId: string | null;
  setEnvironments: (environments: Environment[]) => void;
  setActiveEnvironment: (id: string | null) => void;
  addEnvironment: (environment: Environment) => void;
  updateEnvironment: (id: string, updates: Partial<Environment>) => void;
  deleteEnvironment: (id: string) => void;

  // UI State
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // Theme
  themePreference: ThemePreference;
  setThemePreference: (theme: ThemePreference) => void;

  // Local Request Data (for locally imported requests without backend sync)
  localRequestData: Record<string, HttpRequest>;
  setLocalRequestData: (requestId: string, data: HttpRequest) => void;
  getLocalRequestData: (requestId: string) => HttpRequest | undefined;
  setLocalRequestDataBatch: (data: Record<string, HttpRequest>) => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Workspaces
      workspaces: [],
      activeWorkspaceId: null,
      setWorkspaces: (workspaces) => set({ workspaces }),
      setActiveWorkspace: (id) => set({ activeWorkspaceId: id }),

      // Collections
      collections: [],
      setCollections: (collections) => set({ collections }),
      addCollection: (collection) =>
        set((state) => ({ collections: [...state.collections, collection] })),
      updateCollection: (id, updates) =>
        set((state) => ({
          collections: state.collections.map((c) =>
            c.id === id ? { ...c, ...updates } : c
          ),
        })),
      deleteCollection: (id) =>
        set((state) => ({
          collections: state.collections.filter((c) => c.id !== id),
        })),
      saveRequestToCollection: (collectionId, request, folderId) => {
        const requestId = generateId();
        const collectionRequest: CollectionRequest = {
          id: requestId,
          name: request.name || 'Untitled Request',
          method: request.method,
          folderId,
        };
        set((state) => ({
          collections: state.collections.map((c) => {
            if (c.id !== collectionId) return c;
            if (folderId) {
              // Add to folder
              return {
                ...c,
                folders: c.folders.map((f) =>
                  f.id === folderId
                    ? { ...f, requests: [...f.requests, collectionRequest] }
                    : f
                ),
              };
            }
            // Add to collection root
            return {
              ...c,
              requests: [...c.requests, collectionRequest],
            };
          }),
          // Persist full request data so it can be loaded when clicking the request
          localRequestData: {
            ...state.localRequestData,
            [requestId]: { ...request, id: requestId, name: request.name || 'Untitled Request' },
          },
        }));
        return requestId;
      },

      // Folders
      addFolder: (collectionId, folder) =>
        set((state) => ({
          collections: state.collections.map((c) =>
            c.id === collectionId
              ? { ...c, folders: [...c.folders, folder] }
              : c
          ),
        })),
      updateFolder: (collectionId, folderId, updates) =>
        set((state) => ({
          collections: state.collections.map((c) =>
            c.id === collectionId
              ? {
                  ...c,
                  folders: c.folders.map((f) =>
                    f.id === folderId ? { ...f, ...updates } : f
                  ),
                }
              : c
          ),
        })),
      deleteFolder: (collectionId, folderId, moveRequestsToRoot = true) =>
        set((state) => ({
          collections: state.collections.map((c) => {
            if (c.id !== collectionId) return c;
            const folder = c.folders.find((f) => f.id === folderId);
            const folderRequests = folder?.requests || [];
            return {
              ...c,
              folders: c.folders.filter((f) => f.id !== folderId),
              // Move requests to root if flag is true, otherwise delete them
              requests: moveRequestsToRoot
                ? [...c.requests, ...folderRequests.map((r) => ({ ...r, folderId: undefined }))]
                : c.requests,
            };
          }),
        })),

      // Request Management
      moveRequest: (collectionId, requestId, targetFolderId, sourceFolderId) =>
        set((state) => ({
          collections: state.collections.map((c) => {
            if (c.id !== collectionId) return c;

            let movedRequest: CollectionRequest | undefined;

            // Remove from source
            let updatedFolders = c.folders;
            let updatedRequests = c.requests;

            if (sourceFolderId) {
              // Remove from source folder
              updatedFolders = c.folders.map((f) => {
                if (f.id === sourceFolderId) {
                  movedRequest = f.requests.find((r) => r.id === requestId);
                  return {
                    ...f,
                    requests: f.requests.filter((r) => r.id !== requestId),
                  };
                }
                return f;
              });
            } else {
              // Remove from root
              movedRequest = c.requests.find((r) => r.id === requestId);
              updatedRequests = c.requests.filter((r) => r.id !== requestId);
            }

            if (!movedRequest) return c;

            // Add to target
            if (targetFolderId) {
              // Add to target folder
              updatedFolders = updatedFolders.map((f) =>
                f.id === targetFolderId
                  ? { ...f, requests: [...f.requests, { ...movedRequest!, folderId: targetFolderId }] }
                  : f
              );
            } else {
              // Add to root
              updatedRequests = [...updatedRequests, { ...movedRequest, folderId: undefined }];
            }

            return {
              ...c,
              folders: updatedFolders,
              requests: updatedRequests,
            };
          }),
        })),
      deleteRequest: (collectionId, requestId, folderId) =>
        set((state) => ({
          collections: state.collections.map((c) => {
            if (c.id !== collectionId) return c;
            if (folderId) {
              // Delete from folder
              return {
                ...c,
                folders: c.folders.map((f) =>
                  f.id === folderId
                    ? { ...f, requests: f.requests.filter((r) => r.id !== requestId) }
                    : f
                ),
              };
            }
            // Delete from root
            return {
              ...c,
              requests: c.requests.filter((r) => r.id !== requestId),
            };
          }),
        })),
      updateRequestInCollection: (collectionId, requestId, request) =>
        set((state) => ({
          collections: state.collections.map((c) => {
            if (c.id !== collectionId) return c;
            // Update in folders
            const updatedFolders = c.folders.map((f) => ({
              ...f,
              requests: f.requests.map((r) =>
                r.id === requestId
                  ? { ...r, name: request.name, method: request.method }
                  : r
              ),
            }));
            // Update in root requests
            const updatedRequests = c.requests.map((r) =>
              r.id === requestId
                ? { ...r, name: request.name, method: request.method }
                : r
            );
            return { ...c, folders: updatedFolders, requests: updatedRequests };
          }),
          // Update persisted full request data
          localRequestData: {
            ...state.localRequestData,
            [requestId]: { ...request, id: requestId },
          },
        })),

      // Tabs
      tabs: [createDefaultTab()],
      activeTabId: null,
      openTab: (partial) => {
        const tab: Tab = {
          ...createDefaultTab(),
          ...partial,
          id: partial?.id || generateId(),
        };
        set((state) => {
          // Check if tab already exists
          const existingIndex = state.tabs.findIndex((t) => t.id === tab.id);
          if (existingIndex >= 0) {
            // Update existing tab with new data (e.g., when fetching full request from API)
            const updatedTabs = [...state.tabs];
            updatedTabs[existingIndex] = {
              ...updatedTabs[existingIndex],
              ...partial,
              request: partial?.request || updatedTabs[existingIndex].request,
            };
            return {
              tabs: updatedTabs,
              activeTabId: tab.id
            };
          }
          return {
            tabs: [...state.tabs, tab],
            activeTabId: tab.id,
          };
        });
        return tab.id;
      },
      closeTab: (id) =>
        set((state) => {
          const tabs = state.tabs.filter((t) => t.id !== id);
          let activeTabId = state.activeTabId;

          // If we closed the active tab, activate another
          if (activeTabId === id) {
            const index = state.tabs.findIndex((t) => t.id === id);
            activeTabId = tabs[Math.min(index, tabs.length - 1)]?.id ?? null;
          }

          // Always keep at least one tab
          if (tabs.length === 0) {
            const newTab = createDefaultTab();
            return { tabs: [newTab], activeTabId: newTab.id };
          }

          return { tabs, activeTabId };
        }),
      setActiveTab: (id) => set({ activeTabId: id }),
      updateTab: (id, updates) =>
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === id ? { ...t, ...updates, isDirty: updates.isDirty ?? true } : t
          ),
        })),
      updateTabRequest: (id, updates) =>
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === id
              ? { ...t, request: { ...t.request, ...updates }, isDirty: true }
              : t
          ),
        })),
      setTabResponse: (id, response) =>
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === id ? { ...t, response, isLoading: false } : t
          ),
        })),
      setTabLoading: (id, isLoading) =>
        set((state) => ({
          tabs: state.tabs.map((t) =>
            t.id === id ? { ...t, isLoading } : t
          ),
        })),

      // Environments
      environments: [],
      activeEnvironmentId: null,
      setEnvironments: (environments) => set({ environments }),
      setActiveEnvironment: (id) => set({ activeEnvironmentId: id }),
      addEnvironment: (environment) =>
        set((state) => ({ environments: [...state.environments, environment] })),
      updateEnvironment: (id, updates) =>
        set((state) => ({
          environments: state.environments.map((e) =>
            e.id === id ? { ...e, ...updates } : e
          ),
        })),
      deleteEnvironment: (id) =>
        set((state) => ({
          environments: state.environments.filter((e) => e.id !== id),
          activeEnvironmentId: state.activeEnvironmentId === id ? null : state.activeEnvironmentId,
        })),

      // UI State
      sidebarWidth: 280,
      setSidebarWidth: (width) => set({ sidebarWidth: width }),
      sidebarCollapsed: false,
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      // Theme
      themePreference: 'system',
      setThemePreference: (theme) => set({ themePreference: theme }),

      // Local Request Data
      localRequestData: {},
      setLocalRequestData: (requestId, data) =>
        set((state) => ({
          localRequestData: { ...state.localRequestData, [requestId]: data },
        })),
      getLocalRequestData: (requestId) => get().localRequestData[requestId],
      setLocalRequestDataBatch: (data) =>
        set((state) => ({
          localRequestData: { ...state.localRequestData, ...data },
        })),
    }),
    {
      name: 'snailman-storage',
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        sidebarWidth: state.sidebarWidth,
        sidebarCollapsed: state.sidebarCollapsed,
        activeWorkspaceId: state.activeWorkspaceId,
        activeEnvironmentId: state.activeEnvironmentId,
        environments: state.environments,
        collections: state.collections,
        localRequestData: state.localRequestData,
        themePreference: state.themePreference,
      }),
      // Merge function to handle corrupted localStorage data
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AppState> | undefined;
        return {
          ...currentState,
          ...persisted,
          // Ensure arrays are always arrays (fix corrupted data)
          tabs: Array.isArray(persisted?.tabs) ? persisted.tabs : currentState.tabs,
          environments: Array.isArray(persisted?.environments) ? persisted.environments : currentState.environments,
          collections: Array.isArray(persisted?.collections) ? persisted.collections : currentState.collections,
          workspaces: Array.isArray(persisted?.workspaces) ? persisted.workspaces : currentState.workspaces,
        };
      },
    }
  )
);

// Initialize active tab if none set
const state = useStore.getState();
if (!state.activeTabId && state.tabs.length > 0) {
  useStore.setState({ activeTabId: state.tabs[0].id });
}
